/**
 * MyRecords.tsx — A client's full record set (matched + pending + unmatched).
 *
 * Visible in client and demo View as Client modes; hidden in full admin (that
 * audience gets /client-records). Uses the shared /api/client-records endpoint
 * with mode-aware scoping — the backend enforces which client's records you see.
 *
 * Layout:
 *   - 3-card summary strip (Matched / Pending review / Unmatched)
 *   - Search + status filter
 *   - Paginated table (25/page)
 *   - Clicking the Census ID on a matched row opens the outlet detail page
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { theme } from '../../theme';
import { useAdmin } from '../../AdminContext';
import {
  PageHeader,
  SearchInput, Select,
  Table, TableToolbar, Pagination,
  StatCard, StatGrid,
  Tag,
  EmptyState,
  Icon,
  toast,
} from '../../components/ui';

/* ═══════════════ TYPES ═══════════════ */

interface ClientRecord {
  id: string;
  client_name: string;
  raw_name: string;
  raw_address: string;
  raw_city: string;
  raw_vat: string;
  census_id: string;
  match_method: string;
  match_confidence: number | null;
  match_status: string;
  canonical_name: string;
  canonical_city: string;
  created_at: string | null;
}

interface CountsResponse {
  matched: number;
  pending: number;
  unmatched: number;
}

type StatusKey = 'matched' | 'pending_review' | 'unmatched';

/* ═══════════════ HELPERS ═══════════════ */

const MATCHED_METHODS = new Set(['deterministic', 'fuzzy', 'geo', 'manual_approved', 'llm_approved']);

function getStatus(r: ClientRecord): StatusKey {
  if (r.match_status === 'pending_review') return 'pending_review';
  if (MATCHED_METHODS.has(r.match_method)) return 'matched';
  return 'unmatched';
}

const STATUS_CONFIG: Record<StatusKey, { label: string; tagKind: 'success' | 'warning' | 'danger' }> = {
  matched:        { label: 'Matched',        tagKind: 'success' },
  pending_review: { label: 'Pending review', tagKind: 'warning' },
  unmatched:      { label: 'Unmatched',      tagKind: 'danger'  },
};

const LIMIT = 25;

/* ═══════════════ PAGE ═══════════════ */

export default function MyRecords() {
  const navigate = useNavigate();
  const { modeVersion } = useAdmin();

  const [items, setItems] = useState<ClientRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | StatusKey>('');

  const [counts, setCounts] = useState<CountsResponse>({ matched: 0, pending: 0, unmatched: 0 });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    document.title = 'My Records | Meridian Registry';
    return () => { document.title = 'Meridian Registry'; };
  }, []);

  /* ─── Fetch table data ─── */
  const fetchData = useCallback(async (p: number, signal?: AbortSignal) => {
    setLoading(true);
    const params: Record<string, string | number> = { page: p, limit: LIMIT };
    if (search) params.search = search;
    if (statusFilter) params.match_method = statusFilter;
    try {
      const res = await api.get<{ items: ClientRecord[]; total: number; pages: number }>('/api/client-records', {
        params, signal,
      });
      setItems(res.data.items);
      setTotal(res.data.total);
      setPages(res.data.pages);
      setPage(p);
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'name' in err && (err as { name?: string }).name === 'CanceledError') return;
      if (err && typeof err === 'object' && 'code' in err && (err as { code?: string }).code === 'ERR_CANCELED') return;
      toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    const controller = new AbortController();
    fetchData(1, controller.signal);
    return () => controller.abort();
  }, [fetchData, modeVersion]);

  /* ─── Fetch counts (3 small requests, limit=1 each — same pattern as other pages) ─── */
  useEffect(() => {
    const controller = new AbortController();
    const fetchCount = (method: StatusKey) =>
      api.get<{ total: number }>('/api/client-records', {
        params: { match_method: method, limit: 1 },
        signal: controller.signal,
      }).then(r => r.data.total).catch(() => 0);

    Promise.all([
      fetchCount('matched'),
      fetchCount('pending_review'),
      fetchCount('unmatched'),
    ]).then(([matched, pending, unmatched]) => {
      setCounts({ matched, pending, unmatched });
    });

    return () => controller.abort();
  }, [modeVersion]);

  /* ─── Debounced search ─── */
  const handleSearchInput = (val: string) => {
    setSearchInput(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setSearch(val.trim()); }, 300);
  };

  /* ─── Columns ─── */
  const columns = [
    {
      key: 'raw_name',
      header: 'Name',
      render: (r: ClientRecord) => (
        <span style={{ color: theme.text.primary, fontWeight: theme.fontWeight.medium }}>
          {r.raw_name || '—'}
        </span>
      ),
    },
    {
      key: 'raw_address',
      header: 'Address',
      render: (r: ClientRecord) => (
        <span style={{ color: theme.text.secondary }}>{r.raw_address || '—'}</span>
      ),
    },
    {
      key: 'raw_city',
      header: 'City',
      render: (r: ClientRecord) => (
        <span style={{ color: theme.text.secondary }}>{r.raw_city || '—'}</span>
      ),
    },
    {
      key: 'raw_vat',
      header: 'VAT',
      mono: true,
      render: (r: ClientRecord) => (
        <span style={{
          color: theme.text.secondary,
          fontFamily: theme.font.data,
          fontSize: theme.fontSize.sm,
        }}>{r.raw_vat || '—'}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: 150,
      render: (r: ClientRecord) => {
        const s = getStatus(r);
        const cfg = STATUS_CONFIG[s];
        return <Tag kind={cfg.tagKind} size="sm">{cfg.label}</Tag>;
      },
    },
    {
      key: 'census_id',
      header: 'Census ID',
      width: 140,
      render: (r: ClientRecord) => {
        const s = getStatus(r);
        if (s === 'matched' && r.census_id) {
          return (
            <span
              onClick={(e) => { e.stopPropagation(); navigate(`/outlet/${r.census_id}`); }}
              style={{
                color: theme.brand.cyanLight,
                cursor: 'pointer',
                fontFamily: theme.font.data,
                fontSize: theme.fontSize.sm,
              }}
              onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
              onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
            >
              {r.census_id}
            </span>
          );
        }
        return (
          <span style={{
            color: theme.text.quaternary,
            fontFamily: theme.font.data,
            fontSize: theme.fontSize.sm,
          }}>—</span>
        );
      },
    },
    {
      key: 'match_confidence',
      header: 'Confidence',
      width: 110,
      align: 'right' as const,
      render: (r: ClientRecord) => {
        if (r.match_confidence == null) {
          return <span style={{ color: theme.text.quaternary }}>—</span>;
        }
        const pct = Math.round(r.match_confidence * 100);
        const color = pct >= 90 ? theme.semantic.success
                    : pct >= 65 ? theme.semantic.warning
                    : theme.semantic.danger;
        return (
          <span style={{
            fontFamily: theme.font.data,
            fontSize: theme.fontSize.sm,
            color,
            fontVariantNumeric: 'tabular-nums',
          }}>{pct}%</span>
        );
      },
    },
  ];

  return (
    <div style={{ padding: `${theme.space[8]} ${theme.space[10]}`, maxWidth: 1600, margin: '0 auto' }}>
      <PageHeader
        title="My Records"
        description="Every record you've submitted, with its match status against the Meridian Registry."
        meta={
          <>
            <span style={{ color: theme.text.primary, fontWeight: theme.fontWeight.medium }}>
              {total.toLocaleString()}
            </span>
            <span>records</span>
          </>
        }
      />

      {/* Summary strip */}
      <StatGrid columns="repeat(3, 1fr)">
        <StatCard
          label="Matched"
          value={counts.matched.toLocaleString()}
          delta={counts.matched > 0 ? 'Confirmed against registry' : '—'}
          deltaType="up"
        />
        <StatCard
          label="Pending review"
          value={counts.pending.toLocaleString()}
          delta={counts.pending > 0 ? 'Awaiting decision' : 'All clear'}
          deltaType={counts.pending > 0 ? 'down' : 'neutral'}
        />
        <StatCard
          label="Unmatched"
          value={counts.unmatched.toLocaleString()}
          delta={counts.unmatched > 0 ? 'No Meridian match yet' : 'All matched'}
          deltaType={counts.unmatched > 0 ? 'down' : 'up'}
        />
      </StatGrid>

      <div style={{ height: theme.space[5] }} />

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: theme.space[3],
        marginBottom: theme.space[5],
        flexWrap: 'wrap',
      }}>
        <div style={{ flex: '1 1 280px', maxWidth: 420 }}>
          <SearchInput
            placeholder="Search name, address, city…"
            value={searchInput}
            onChange={e => handleSearchInput(e.target.value)}
            kbd={undefined}
          />
        </div>
        <div style={{ minWidth: 200 }}>
          <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value as '' | StatusKey)}>
            <option value="">All statuses</option>
            <option value="matched">Matched</option>
            <option value="pending_review">Pending review</option>
            <option value="unmatched">Unmatched</option>
          </Select>
        </div>
      </div>

      {/* Toolbar */}
      <TableToolbar>
        <div style={{ color: theme.text.tertiary, fontSize: theme.fontSize.sm }}>
          Showing{' '}
          <span style={{ color: theme.text.primary, fontFamily: theme.font.data, fontWeight: theme.fontWeight.medium }}>
            {items.length.toLocaleString()}
          </span>
          {' '}of{' '}
          <span style={{ color: theme.text.primary, fontFamily: theme.font.data, fontWeight: theme.fontWeight.medium }}>
            {total.toLocaleString()}
          </span>
        </div>
        <Pagination page={page} totalPages={pages} onChange={p => fetchData(p)} />
      </TableToolbar>

      <Table
        data={items}
        columns={columns}
        loading={loading}
        minWidth={960}
        rowKey={r => r.id}
        emptyState={
          <EmptyState
            icon={<Icon.Database size={18} />}
            title="No records to show"
            description={
              search || statusFilter
                ? 'Try clearing the filters or search.'
                : "You haven't submitted any records yet."
            }
          />
        }
      />
    </div>
  );
}
